/**
 * Stale-deploy chunk recovery.
 *
 * THE FAILURE (observed in production 2026-07-26, blank Account page; root
 * cause pinned down in docs/AUDIT-2026-10-15-stability.md, finding C1):
 * every route is a lazy, hash-named chunk. src/sw.js calls skipWaiting(), and
 * per the service-worker spec that hands EVERY tab the registration already
 * controls to the new worker the moment it activates — clients.claim() only
 * matters for pages no worker controls at all. Workbox's activate step then
 * deletes every precached URL that is not in the new manifest. So after any
 * deploy, a tab still running the previous bundle has lost the route chunks it
 * had not loaded yet. Its next tap on a site, keg or report asks the network
 * for a chunk hash that no longer exists; Netlify's SPA fallback answers with
 * `index.html`, HTTP 200, `content-type: text/html`, and webpack tries to run
 * HTML as JavaScript:
 *
 *   ChunkLoadError: Loading chunk 7 failed.
 *     (missing: https://dashboard.kegtron.com/route-account.chunk.fd45f.esm.js)
 *   Uncaught SyntaxError: Unexpected token '<'
 *
 * preact-cli's async loader has no error path: the route simply never mounts
 * and the content area stays blank. It affects only the routes the tab had
 * not already loaded, which is why it looks like "one page is broken" rather
 * than "the app is stale".
 *
 * THE FIX, in two layers:
 *  1. Treat the error as "this tab is running an old build" and reload ONCE
 *     PER BUILD ID per tab, via adoptNewBuild() so the navigation is served by
 *     the fresh worker. The guard used to be once per tab session, with a flag
 *     that was never cleared — a pinned tab or a home-screen PWA kept it across
 *     reloads, so the second deploy it lived through left it blank for good.
 *     Keying the flag on the build id that was running when we reloaded keeps
 *     the "never loop" guarantee (a reload that lands on the SAME build again
 *     must not reload a second time) while letting a tab that later outlives
 *     another deploy recover again.
 *  2. When a reload is not allowed (the same build already got its one
 *     attempt), publish the failure so the content area renders a visible
 *     message with a Reload button instead of nothing. See
 *     components/chunk-error-notice.
 *
 * This complements versionCheckService (which politely prompts on a new
 * deploy). This path is the hard failure that needs no prompt: the page is
 * already broken, so reloading strictly improves matters.
 */

import { getBuildVersion, adoptNewBuild } from '../services/versionCheckService';

export const RECOVERY_FLAG = 'kegtron_chunk_recovery_attempted';

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  // HTML served where JS was expected — the SPA-fallback symptom above.
  /Unexpected token '</,
  /Unexpected token </,
];

/**
 * True when an error indicates a missing/stale webpack chunk rather than an
 * ordinary application bug. Deliberately narrow: a false positive would reload
 * the app out from under a user for an unrelated exception.
 *
 * @param {Error|string|null|undefined} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (typeof error === 'string') {
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(error));
  }
  if (error.name === 'ChunkLoadError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * The recovery record for this tab, or null. `build` is the build id that was
 * running when the automatic reload fired; `at` is when. A value written by
 * the pre-2026-10 per-session version was a bare timestamp — read as an
 * attempt under an unknown build, which never blocks a new build's attempt.
 *
 * @returns {{build: string|null, at: number|null}|null}
 */
export function readRecoveryFlag() {
  try {
    return parseRecoveryFlag(window.sessionStorage.getItem(RECOVERY_FLAG));
  } catch (err) {
    return null;
  }
}

function parseRecoveryFlag(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return { build: parsed.build || null, at: Number(parsed.at) || null };
    }
  } catch (err) {
    // legacy bare-timestamp value — handled below
  }
  return { build: null, at: Number(raw) || null };
}

/**
 * One automatic reload per build id per tab. Returns true if a reload should
 * be performed now, and records the attempt against `buildId`.
 *
 * sessionStorage (not localStorage) on purpose: a new tab should be free to
 * try again, but a reload that lands this tab back on the same build must not
 * loop — the error has to surface instead (see CLAUDE.md "Deploying
 * browser-persisted changes": a bad deploy must self-heal, never wedge).
 *
 * @param {string} [buildId] the build this tab is running (defaults to the baked-in id)
 * @returns {boolean}
 */
export function shouldAttemptRecovery(buildId = getBuildVersion()) {
  try {
    // Read storage directly, NOT via readRecoveryFlag (which maps a storage
    // error to "no record"): a throw here must refuse the reload below.
    const prior = parseRecoveryFlag(window.sessionStorage.getItem(RECOVERY_FLAG));
    if (prior && prior.build === buildId) return false;
    window.sessionStorage.setItem(RECOVERY_FLAG, JSON.stringify({ build: buildId, at: Date.now() }));
    return true;
  } catch (err) {
    // Storage blocked (e.g. "block all site data" throws SecurityError): we
    // cannot remember that we already reloaded, and every reload starts a
    // fresh page, so an automatic reload here could loop forever on a chunk
    // that is genuinely missing from the deployed build. Never reload blind —
    // the caller shows the notice and the user's own click does the reload.
    return false;
  }
}

/**
 * A route has mounted. If this boot is running a DIFFERENT build than the one
 * the recorded reload fired under, that recovery succeeded and its record is
 * spent — clear it so the tab's state is tidy and inspectable. A record for
 * the SAME build is kept on purpose: the reload did not adopt a new build, so
 * the next chunk failure must show the notice, not reload again.
 *
 * Housekeeping only: shouldAttemptRecovery already allows a reload whenever
 * the recorded build differs from the running one, so clearing (or failing to
 * clear) the record never changes whether the next failure reloads.
 *
 * @param {string} [buildId]
 */
export function markRouteMounted(buildId = getBuildVersion()) {
  try {
    const prior = readRecoveryFlag();
    if (prior && prior.build !== buildId) {
      window.sessionStorage.removeItem(RECOVERY_FLAG);
    }
  } catch (err) {
    // storage unavailable — nothing to clear
  }
}

// ---------------------------------------------------------------------------
// Failure channel: the content area subscribes and renders the notice.
// ---------------------------------------------------------------------------

let failure = null;
let reloadScheduled = false;
const listeners = new Set();

/** @returns {{error: Error|string, at: number}|null} */
export function getChunkFailure() {
  return failure;
}

/**
 * @param {(failure: object|null) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeChunkFailure(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(next) {
  failure = next;
  listeners.forEach((listener) => listener(failure));
}

/** Called on every route change: the next route may well load fine. */
export function clearChunkFailure() {
  if (failure) publish(null);
}

/**
 * Reload onto the deployed build. adoptNewBuild() waits (bounded) for a
 * freshly-installing worker to activate before navigating; in the C1 case the
 * new worker is usually ALREADY active (that is why the chunks are gone), so
 * the wait returns at once and the reload is served by the new precache.
 */
function recover() {
  const fallback = () => window.location.reload();
  try {
    Promise.resolve(adoptNewBuild()).catch(fallback);
  } catch (err) {
    fallback();
  }
}

/**
 * Decide what to do about an error that may be a chunk failure. Returns true
 * when the error was a chunk failure (handled), false when it was unrelated.
 *
 * @param {Error|string|null|undefined} error
 * @returns {boolean}
 */
export function handleChunkError(error) {
  if (!isChunkLoadError(error)) return false;
  // A failing chunk raises both the SyntaxError and the ChunkLoadError; once a
  // reload is on its way, nothing else this page does matters.
  if (reloadScheduled) return true;
  if (shouldAttemptRecovery()) {
    reloadScheduled = true;
    recover();
    return true;
  }
  publish({ error, at: Date.now() });
  return true;
}

/**
 * Install global handlers. Safe to call once at startup; a no-op outside a
 * browser.
 */
export function installChunkErrorRecovery() {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

  window.addEventListener('unhandledrejection', (event) => {
    handleChunkError(event && event.reason);
  });

  window.addEventListener('error', (event) => {
    handleChunkError(event && (event.error || event.message));
  });
}

/** Test hook: forget the page-level latch and any published failure. */
export function resetChunkRecoveryForTests() {
  failure = null;
  reloadScheduled = false;
  listeners.clear();
}

export default installChunkErrorRecovery;
